import { useEffect, useRef, useState } from 'react';

import { useResponsive } from '@depop/web-ui-kit/useResponsive';

import { handleSSOError, setAccountLinkingCookies } from '../login/helpers';

import { decodeJWT } from '@/modules/auth/decodeJWT';
import { initGoogleIdentityClient } from '@/modules/auth/google';
import { useGoogleIdentityService } from '@/modules/auth/useGoogleIdentityService';
import { useRedirectQueryParam } from '@/modules/auth/useRedirectQueryParam';
import { TRANSCEND_VIEW_STATE_CHANGE_EVENT_NAME } from '@/modules/consent/constants';
import { useConsentManager } from '@/modules/consent/hooks/useConsentManager';
import { isPreviewEnv } from '@/modules/env/isPreviewEnv';
import { LoginType } from '@/modules/login/types';
import { useLoginMutations } from '@/modules/login/useLoginMutations';
import { useExtendedRouter } from '@/modules/routing/useExtendedRouter';
import { useSSOSignup } from '@/modules/signup/hooks/useSSOSignup';
import { SignupType } from '@/modules/signup/types';
import { useCurrentUser } from '@/modules/user/useCurrentUser';

type Params = {
  nonce: string;
};

export function useGoogleSignInPrompt({ nonce }: Params) {
  const redirectQueryParam = useRedirectQueryParam();
  const router = useExtendedRouter();
  const [isAuthenticated] = useCurrentUser();
  const { isMaxSmall } = useResponsive();
  const { isGoogleIdentityLoaded } = useGoogleIdentityService(nonce);
  const { signupWithSSO } = useSSOSignup({
    ssoType: SignupType.Google,
  });
  const { loginMutation } = useLoginMutations({
    loginType: LoginType.GoogleSignup,
  });

  const { transcend, airgap } = useConsentManager();
  const [transcendCookieBannerVisible, setTranscendCookieBannerVisible] =
    useState(false);

  function handleViewStateChange(event: CustomEvent) {
    const { detail } = event;
    const { viewState } = detail;
    setTranscendCookieBannerVisible(viewState !== 'Hidden');
  }

  useEffect(() => {
    if (transcend) {
      const currentViewState = transcend.getViewState();
      if (currentViewState) {
        setTranscendCookieBannerVisible(currentViewState !== 'Hidden');
      }

      transcend.addEventListener(
        TRANSCEND_VIEW_STATE_CHANGE_EVENT_NAME,
        handleViewStateChange
      );
    }

    return () => {
      if (transcend) {
        transcend.removeEventListener(
          TRANSCEND_VIEW_STATE_CHANGE_EVENT_NAME,
          handleViewStateChange
        );
      }
    };
  }, [transcend]);

  async function handleGoogleIdentityResponse({
    credential,
  }: google.accounts.id.CredentialResponse) {
    const decodedUserInfo = decodeJWT(credential);
    const { email, given_name, family_name } = decodedUserInfo;

    function handleUserNotFound() {
      signupWithSSO({
        name: {
          firstName: given_name,
          lastName: family_name,
        },
        email,
        credentials: { googleIdToken: credential },
      });
    }

    function handleAccountNotLinked() {
      setAccountLinkingCookies({ googleIdToken: credential }, email);

      router.push(`/login/${redirectQueryParam}`);
    }

    loginMutation.mutate(
      { google_id_token: credential },
      {
        onError: (error) => {
          if ('code' in error.response.data) {
            return handleSSOError({
              error,
              onUserNotFound: handleUserNotFound,
              onAccountNotLinked: handleAccountNotLinked,
            });
          }

          router.push(`/login/${redirectQueryParam}`);
        },
      }
    );
  }

  const hasPromptedRef = useRef(false);
  const responseHandlerRef = useRef(handleGoogleIdentityResponse);

  useEffect(() => {
    responseHandlerRef.current = handleGoogleIdentityResponse;
  });

  useEffect(() => {
    if (hasPromptedRef.current) {
      return;
    }

    if (!isGoogleIdentityLoaded || isPreviewEnv() || isAuthenticated) {
      return;
    }

    if (isMaxSmall) {
      // The view state change event never fires when consent was set on an
      // earlier visit, so confirmed consent is what tells us the banner will
      // not appear at all.
      if (transcendCookieBannerVisible || !airgap?.getConsent()?.confirmed) {
        return;
      }
    }

    const googleId = window.google?.accounts?.id;

    if (typeof googleId?.prompt !== 'function') {
      return;
    }

    // FedCM allows one outstanding navigator.credentials.get call per document,
    // and One Tap keeps its call outstanding until the visitor accepts or
    // dismisses the prompt.
    hasPromptedRef.current = true;
    initGoogleIdentityClient((response) =>
      responseHandlerRef.current(response)
    );
    googleId.prompt();
  }, [
    isGoogleIdentityLoaded,
    isAuthenticated,
    isMaxSmall,
    transcendCookieBannerVisible,
    airgap,
  ]);

  useEffect(() => {
    return () => {
      window.google?.accounts?.id?.cancel?.();
    };
  }, []);
}
